import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {FilterBuilder, FilterOperator} from "@core/shared/models/filter";
import {FilterCollectionField} from "@core/shared/models/filter/filter-collection";
import {ArrayFilterField} from "@core/shared/models/filter/array-filter-field";

@Component({
    selector: 'app-core-checkbox-group-list-filter',
    templateUrl: './checkbox-group-list-filter.component.html',
    styleUrl: './checkbox-group-list-filter.component.scss',
    standalone: false
})
export class CheckboxGroupListFilterComponent<T> implements OnInit, OnChanges {

    @Input() builder: FilterBuilder;

    @Input() label: string;

    @Input() key: string;

    @Input() itemValueProperty: string;

    @Input() itemLabelProperty: string;

    @Input() itemChildOptionsProperty: string;

    @Input() parentOptions: T[] = [];

    @Input() operator: FilterOperator;

    @Input() disableAutoSubmit: boolean;

    @Input() defaultCountDisplayedParentOptions: number = 4;

    @Output() selectionChanged: EventEmitter<T[]> = new EventEmitter<T[]>();

    @Output() valuesUpdated: EventEmitter<T[]> = new EventEmitter<T[]>();

    public values: T[] = [];

    public filterCollectionField: FilterCollectionField;

    public allParentOptionsDisplayed: boolean = false;

    ngOnInit(): void {

        this.filterCollectionField = new FilterCollectionField(this.key);

        this.filterCollectionField.choices = this._retrieveAllOptions(this.parentOptions);

        this.builder.addField(this.filterCollectionField);
    }

    ngOnChanges(changes: SimpleChanges) {

        if(('parentOptions' in changes) && !changes.parentOptions.isFirstChange()){

            this.filterCollectionField.choices = this._retrieveAllOptions(changes.parentOptions.currentValue);
        }
    }

    private _retrieveAllOptions(parentOptions: T[]): T[] {

        const options: T[] = [];

        parentOptions.forEach((parentOption: T): void => {

            options.push(parentOption);

            options.push(...this.retrieveChildOptionsFromParentOption(parentOption));
        });

        return options;
    }

    public handleItemSelection(item: T): void {

        if(this.isSelectedItem(item)){

            const index: number = this.values.findIndex((value: T): boolean => {

                return this.getKeyPropertyValue(value) === this.getKeyPropertyValue(item);
            });

            this.values.splice(index, 1);
        }
        else{

            this.values.push(item);
        }

        this.valuesUpdated.emit(this.values);
    }

    public handleParentSelection(item: T, emitEvent: boolean = true): void {

        this.handleItemSelection(item);

        if(!emitEvent){

            return;
        }

        if(this.isSelectedItem(item)){

            const childOptions: T[] = this.retrieveChildOptionsFromParentOption(item).filter((childOption: T): boolean => {

                return !this.isSelectedItem(childOption);
            });

            childOptions.forEach((childOption: T): void => {

                this.handleChildSelection(childOption, item, false);
            });
        }
        else{

            const childOptions: T[] = this.retrieveChildOptionsFromParentOption(item).filter((childOption: T): boolean => {

                return this.isSelectedItem(childOption);
            });

            childOptions.forEach((childOption: T): void => {

                this.handleChildSelection(childOption, item, false);
            });
        }
    }

    public handleChildSelection(item: T, parentItem: T, emitEvent: boolean = true): void {

        this.handleItemSelection(item);

        if(!emitEvent){

            return;
        }

        if(this.isSelectedItem(item)){

            if(!this.isSelectedItem(parentItem)){

                this.handleParentSelection(parentItem, false);
            }
        }
        else{

            const isEmptyChildSelection: boolean = this.retrieveChildOptionsFromParentOption(parentItem).every((childOption: T): boolean => {

                return !this.isSelectedItem(childOption);
            });

            if(this.isSelectedItem(parentItem) && isEmptyChildSelection){

                this.handleParentSelection(parentItem, false);
            }
        }
    }

    public filter(): void {

        this.hydrateValue();

        this.selectionChanged.emit(this.values);

        if (this.disableAutoSubmit) {

            return;
        }

        this.builder.filter();
    }

    public hydrateValue(): void {

        this.filterCollectionField.clear();

        this.values.forEach((value: T): void => {

            this.filterCollectionField.fields.push(new ArrayFilterField(this.key, this.operator, value));
        });
    }

    public reset(): void {

        this.resetValues();

        this.filterCollectionField.clear();
    }

    public resetValues(): void {

        this.values = [];
    }

    public isSelectedItem(item: T): boolean {

        return this.values.some((value: T): boolean => {

            return this.getKeyPropertyValue(value) === this.getKeyPropertyValue(item);
        });
    }

    public getLabelPropertyValue(item: T): string {

        return this.itemLabelProperty.split('.').reduce((carry, key) => carry[key], item);
    }

    public getKeyPropertyValue(item: T): any {

        return this.itemValueProperty.split('.').reduce((carry, key) => carry[key], item);
    }

    public toggleParentOptionDisplay(): void {

        this.allParentOptionsDisplayed = !this.allParentOptionsDisplayed;
    }

    public isParentOptionDisplayed(index: number): boolean {

        if(this.allParentOptionsDisplayed){

            return true;
        }

        return index < this.defaultCountDisplayedParentOptions;
    }

    public retrieveChildOptionsFromParentOption(parentOption: T): T[] {

        return parentOption[this.itemChildOptionsProperty];
    }

    get displayedParentOptions(): T[] {

        return this.parentOptions.filter((item: T, index: number): boolean => {

            return this.isParentOptionDisplayed(index);
        });
    }

    get displayToggleParentOptionsAction(): boolean {

        return this.parentOptions.length > this.defaultCountDisplayedParentOptions;
    }
}
